import React from 'react'
import Status from 'Components/DesignSystem/Status/Status';
import { ChevronDown } from 'Components/DesignSystem/Iconography/Icons';

const ReviewDropdown = ({
    value,
    onChange,
    disabled,
    itemId
}: {
    value: boolean;
    onChange: (value: boolean) => void;
    disabled: boolean;
    itemId: string;
}) => {

    if (!disabled) {
        /*
         * Edit mode — stays a native <select> (its keyboard/change semantics are
         * exactly what the flow needs); only the inks moved onto the tone tokens
         * the DS Status grammar uses, so the control themes (2026-09-05).
         *
         * § 324 — it is now shaped like the DS `Status` chip it sits beside, because
         * the Review column renders BOTH: a pending row is this <select>, an already
         * reviewed row falls through to the chip below (`disabled` is
         * `!isEditMode || reviewed`). The two were different widgets in one column —
         * 28px tall against 20px, a 1px outline against none, 12px/28px padding
         * against 8px/8px — so the column read as ragged rather than as one thing.
         *
         * The metrics below are the chip's, copied deliberately rather than shared:
         * `Status` exposes no way to render its chip shape around arbitrary children,
         * and inlining this control into `Status` would drag a form control into a
         * presentational component. If the chip's grammar moves, this moves with it.
         *
         * Two things are NOT copied, both on purpose:
         *   - `tw-pe-6` rather than the chip's `tw-pe-2`, to leave room for the
         *     chevron. It makes this ~16px wider than the chip; that width IS the
         *     affordance, and it is the only shape difference left.
         *   - no fixed width. It was `tw-w-[120px]`, which left 120-12-28 = 80px of
         *     text room for a 12px "Review Pending" that needs ~88 — so the label
         *     rendered as "Review Pendin" (reported 2026-09-22). A <select> sizes to
         *     its widest option, so letting it do that cannot clip.
         */
        return (
            <div className="tw-relative tw-inline-block">
                <select
                    value={value ? 'reviewed' : 'pending'}
                    onChange={(e) => onChange(e.target.value === 'reviewed')}
                    className={`
                    tw-appearance-none tw-border-0 tw-cursor-pointer
                    tw-ps-2 tw-pe-6 tw-py-0.5 tw-min-h-5 tw-rounded-ctl
                    tw-whitespace-nowrap tw-text-sub-body-supporting
                    ${value
                            ? 'tw-bg-tone-success-tint tw-text-tone-success-ink'
                            : 'tw-bg-tone-warning-tint tw-text-tone-warning-ink'
                        }
                `}
                    data-testid={`${itemId}-review-dropdown`}
                >
                    <option value="pending">Review Pending</option>
                    <option value="reviewed">Reviewed</option>
                </select>

                {/* `-translate-y-1/2` is what centres an absolutely positioned box on
                    `top-1/2`: you move it up by HALF its own height. It was `-1/4`,
                    which left the 16px chevron 4px below the centre line. */}
                <div className={`tw-absolute tw-right-1.5 tw-top-1/2 tw--translate-y-1/2 tw-pointer-events-none ${value ? 'tw-text-tone-success-ink' : 'tw-text-tone-warning-ink'}`}>
                    <ChevronDown size={16} />
                </div>
            </div>
        );
    }

    // View mode — the DS Status chip, same tone mapping as the Review Queue's
    // status pill (Reviewed -> success, Review Pending -> warning).
    return (
        <Status
            tone={value ? 'success' : 'warning'}
            label={value ? 'Reviewed' : 'Review Pending'}
        />
    );
};

export default ReviewDropdown
